import React, { useEffect, useMemo, useState } from 'react';

import { createDocumentRoute, mentionType } from '@aline/common';
import { link } from '@aline/routes';
import { Checkbox, Collapse, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronDown, ChevronUp, ListTodo, Package, Plus, Sparkles, Workflow } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import { ReportIcon } from 'src/components/icons/ReportIcon';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers/url';
import { alineLinkButtonStylesWithUnderline, createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';

import { $chatThreadState } from '../../chat-state';
import type {
  DocumentMentionElement,
  ReportMentionElement,
  TaskMentionElement,
  TMentionElement,
  WorkflowMentionElement,
} from '../ChatEditor';
import type { PinnedSkill } from '../ChatEditor/ChatEditorSkill/utils';

/** What the bar adds to the composer: mention chips, then skill chips. */
export interface PromptInsertion {
  mentions: TMentionElement[];
  skills: { scope: Apollo.AgentSkillScope; skill: PinnedSkill }[];
}

type ArtifactRow = NonNullable<Apollo.GetAgentThreadQuery['agentThread']>['artifacts'][number];
type DocumentArtifactRow = Extract<ArtifactRow, { __typename: 'ThreadDocumentArtifact' }>;
type ReportArtifactRow = Extract<ArtifactRow, { __typename: 'ThreadReportArtifact' }>;
type TaskArtifactRow = Extract<ArtifactRow, { __typename: 'ThreadTaskArtifact' }>;
type WorkflowArtifactRow = Extract<ArtifactRow, { __typename: 'ThreadWorkflowArtifact' }>;
type SkillArtifactRow = Extract<ArtifactRow, { __typename: 'ThreadSkillArtifact' }>;
type MentionableArtifactRow =
  | DocumentArtifactRow
  | ReportArtifactRow
  | TaskArtifactRow
  | WorkflowArtifactRow;
type SelectableArtifactRow = MentionableArtifactRow | SkillArtifactRow;

const UNPUBLISHED_SKILL_TOOLTIP = 'Publish this skill to insert it into a prompt.';

const ROLE_LABEL_BY_KIND = {
  [Apollo.AgentMessageJoinKind.Created]: 'Created',
  [Apollo.AgentMessageJoinKind.Mentioned]: 'Mentioned',
  [Apollo.AgentMessageJoinKind.Drafted]: 'Drafted',
  [Apollo.AgentMessageJoinKind.Edited]: 'Redlined',
} as const satisfies Record<Apollo.AgentMessageJoinKind, string>;

const useStyles = makeStyles()(theme => ({
  bulkActionButton: {
    color: '#324a52',
  },
  bulkActions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
  },
  bulkActionsHidden: {
    pointerEvents: 'none',
    visibility: 'hidden',
  },
  header: {
    alignItems: 'center',
    background: alpha(theme.appColors.primary, 0.04),
    border: 'none',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 2, 1, 2.5),
    textAlign: 'left',
    width: '100%',
  },
  headerChevron: {
    color: theme.appColors.primary,
    marginLeft: 'auto',
  },
  headerIcon: {
    color: theme.appColors.primary,
  },
  headerLabel: {
    color: theme.appColors.primary,
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold,
  },
  panel: {
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 280,
    overflowY: 'auto',
  },
  root: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderBottom: 'none',
    borderRadius: `${theme.spacing(1)} ${theme.spacing(1)} 0 0`,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
  },
  row: {
    '&:last-child': { borderBottom: 'none' },
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    gap: theme.spacing(0.5),
    minHeight: 36,
    padding: theme.spacing(0.5, 2),
  },
  rowCheckbox: {
    padding: 2,
  },
  rowIcon: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'inline-flex',
    flexShrink: 0,
  },
  rowRoles: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontStyle: 'italic',
    minWidth: 96,
    textAlign: 'right',
  },
  rowTitleLink: {
    ...alineLinkButtonStylesWithUnderline,
    ...createClampStyles(2),
    marginRight: 'auto',
  },
  selectAllLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold,
  },
  selectAllRow: {
    alignItems: 'center',
    /**
     * Faint blue-tinted background distinguishing the bulk-action row
     * from the artifact rows below. No theme token matches this exact
     * shade; kept as a literal until the theme grows a "subtle-tinted
     * surface" token.
     */
    background: alpha(theme.appColors.primary, 0.02),
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
  },
}));

/**
 * Collapsible bar anchored above the chat composer that lists every
 * artifact (mentioned / drafted / edited document) linked to a thread
 * via `AgentMessageDocumentJoin`. The server dedupes by document and
 * picks the strongest role per doc (drafted > edited > mentioned), so
 * the bar can render the query result verbatim — no client-side fence
 * parsing, no flattening, no localStorage caching.
 */
export const ThreadDeliverablesBar = React.forwardRef<
  HTMLDivElement,
  {
    /** Deduped artifacts for the thread, in display order. */
    artifacts: readonly ArtifactRow[];
    className?: string;
    /** Insert a chip per selected artifact into the chat composer. */
    onInsertIntoPrompt?: (insertion: PromptInsertion) => void;
    style?: React.CSSProperties;
  }
>(function ThreadDeliverablesBar({ artifacts, className, onInsertIntoPrompt, style }, ref) {
  const { classes, cx } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { inputPopoverOpenCount } = useSnapshot($chatThreadState);
  /**
   * Composite `${agentMessageId}:${documentId}` key — artifact rows are
   * unique per message/document and carry one or more roles in kindList.
   */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  /**
   * Auto-collapse whenever the chat input opens one of its popovers
   * (such as history) so the bar doesn't hover
   * over the just-opened panel. The `> 0` guard skips the initial
   * mount: without it, the effect collapsed the bar on every re-mount
   * (thread switches) even though no popover had just opened.
   */
  useEffect(() => {
    if (inputPopoverOpenCount > 0) setExpanded(false);
  }, [inputPopoverOpenCount]);

  /**
   * Soft-deleted docs intentionally remain in the bar — Trash is
   * recoverable. Hard-deleted docs never reach this list (their join row
   * is CASCADE'd away by Postgres).
   */
  const documentArtifacts = useMemo(() => artifacts.filter(getIsDocumentArtifact), [artifacts]);
  const reportArtifacts = useMemo(() => artifacts.filter(getIsReportArtifact), [artifacts]);
  const taskArtifacts = useMemo(() => artifacts.filter(getIsTaskArtifact), [artifacts]);
  const workflowArtifacts = useMemo(() => artifacts.filter(getIsWorkflowArtifact), [artifacts]);
  const skillArtifacts = useMemo(() => artifacts.filter(getIsSkillArtifact), [artifacts]);
  const selectableArtifacts = useMemo<SelectableArtifactRow[]>(
    () => [
      ...documentArtifacts,
      ...reportArtifacts,
      ...taskArtifacts,
      ...workflowArtifacts,
      ...skillArtifacts.filter(row => Boolean(getPinnedSkill(row))),
    ],
    [documentArtifacts, reportArtifacts, skillArtifacts, taskArtifacts, workflowArtifacts]
  );
  const artifactKeys = useMemo(
    () => new Set(selectableArtifacts.map(selectableRowKey)),
    [selectableArtifacts]
  );

  useEffect(() => {
    setSelected(prev => {
      const next = new Set([...prev].filter(key => artifactKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [artifactKeys]);

  const selectedEntries = useMemo(
    () => selectableArtifacts.filter(e => selected.has(selectableRowKey(e))),
    [selectableArtifacts, selected]
  );
  if (artifacts.length === 0) return null;

  const selectedVisibleCount = selectedEntries.length;
  const allSelected =
    selectedVisibleCount > 0 && selectedVisibleCount === selectableArtifacts.length;
  const someSelected = selectedVisibleCount > 0 && !allSelected;
  const totalCount = artifacts.length;

  function toggleOne(key: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll() {
    setSelected(prev =>
      selectableArtifacts.every(entry => prev.has(selectableRowKey(entry)))
        ? new Set()
        : new Set(selectableArtifacts.map(selectableRowKey))
    );
  }

  function handleInsertIntoPrompt() {
    if (!onInsertIntoPrompt || selectedEntries.length === 0) return;
    onInsertIntoPrompt(toPromptInsertion(selectedEntries));
    setSelected(new Set());
  }

  const hasSelection = selectedVisibleCount > 0;

  return (
    <div ref={ref} className={cx(classes.root, className)} style={style}>
      <button
        aria-expanded={expanded}
        className={classes.header}
        type="button"
        onClick={() => setExpanded(prev => !prev)}
      >
        <Package className={classes.headerIcon} size={16} />
        <Typography className={classes.headerLabel}>
          {totalCount} {totalCount === 1 ? 'Artifact' : 'Artifacts'}
        </Typography>
        {expanded ? (
          <ChevronDown className={classes.headerChevron} size={20} />
        ) : (
          <ChevronUp className={classes.headerChevron} size={20} />
        )}
      </button>
      <Collapse unmountOnExit in={expanded} timeout="auto">
        <div className={classes.panel}>
          <div className={classes.selectAllRow}>
            <Checkbox
              checked={allSelected}
              className={classes.rowCheckbox}
              indeterminate={someSelected}
              size="small"
              onChange={toggleAll}
            />
            <Typography className={classes.selectAllLabel}>Select All</Typography>
            <div className={cx(classes.bulkActions, !hasSelection && classes.bulkActionsHidden)}>
              <BulkActionButton
                icon={<Plus size={16} />}
                label="Insert into prompt"
                onClick={handleInsertIntoPrompt}
              />
            </div>
          </div>
          {documentArtifacts.map(row => (
            <DeliverableRow
              key={rowKey(row)}
              row={row}
              selected={selected.has(selectableRowKey(row))}
              onToggle={() => toggleOne(rowKey(row))}
            />
          ))}
          {reportArtifacts.map(row => (
            <ReportDeliverableRow
              key={reportRowKey(row)}
              row={row}
              selected={selected.has(selectableRowKey(row))}
              onToggle={() => toggleOne(reportRowKey(row))}
            />
          ))}
          {taskArtifacts.map(row => (
            <TaskDeliverableRow
              key={taskRowKey(row)}
              row={row}
              selected={selected.has(selectableRowKey(row))}
              onToggle={() => toggleOne(taskRowKey(row))}
            />
          ))}
          {workflowArtifacts.map(row => (
            <WorkflowDeliverableRow
              key={workflowRowKey(row)}
              row={row}
              selected={selected.has(selectableRowKey(row))}
              onToggle={() => toggleOne(workflowRowKey(row))}
            />
          ))}
          {skillArtifacts.map(row => (
            <SkillDeliverableRow
              key={skillRowKey(row)}
              row={row}
              selected={selected.has(skillRowKey(row))}
              onToggle={() => toggleOne(skillRowKey(row))}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
});

function DeliverableRow({
  onToggle,
  row,
  selected,
}: {
  onToggle: () => void;
  row: DocumentArtifactRow;
  selected: boolean;
}) {
  const { classes } = useStyles();
  const roleText = formatArtifactRoles(row);

  return (
    <div className={classes.row}>
      <Checkbox
        checked={selected}
        className={classes.rowCheckbox}
        size="small"
        onChange={onToggle}
      />
      <span className={classes.rowIcon}>
        <ContractIcon height={16} width={16} />
      </span>
      <Typography className={classes.rowTitleLink} onClick={() => openArtifactRow(row)}>
        {row.document.name || 'Untitled Document'}
      </Typography>
      <Typography className={classes.rowRoles} component="span" variant="caption">
        {roleText}
      </Typography>
    </div>
  );
}

function ReportDeliverableRow({
  onToggle,
  row,
  selected,
}: {
  onToggle: () => void;
  row: ReportArtifactRow;
  selected: boolean;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.row}>
      <Checkbox
        checked={selected}
        className={classes.rowCheckbox}
        size="small"
        onChange={onToggle}
      />
      <span className={classes.rowIcon}>
        <ReportIcon height={16} width={16} />
      </span>
      <Typography
        className={classes.rowTitleLink}
        onClick={() => navigate(link.ReportDetail({ reportId: row.report.id }))}
      >
        {row.report.name || 'Untitled Report'}
      </Typography>
      <Typography className={classes.rowRoles} component="span" variant="caption">
        Report
      </Typography>
    </div>
  );
}

function TaskDeliverableRow({
  onToggle,
  row,
  selected,
}: {
  onToggle: () => void;
  row: TaskArtifactRow;
  selected: boolean;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.row}>
      <Checkbox
        checked={selected}
        className={classes.rowCheckbox}
        size="small"
        onChange={onToggle}
      />
      <span className={classes.rowIcon}>
        <ListTodo size={16} />
      </span>
      <Typography className={classes.rowTitleLink}>{row.task.title || 'Untitled Task'}</Typography>
      <Typography className={classes.rowRoles} component="span" variant="caption">
        Task
      </Typography>
    </div>
  );
}

function WorkflowDeliverableRow({
  onToggle,
  row,
  selected,
}: {
  onToggle: () => void;
  row: WorkflowArtifactRow;
  selected: boolean;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.row}>
      <Checkbox
        checked={selected}
        className={classes.rowCheckbox}
        size="small"
        onChange={onToggle}
      />
      <span className={classes.rowIcon}>
        <Workflow size={16} />
      </span>
      <Typography
        className={classes.rowTitleLink}
        onClick={() => navigate(link.WorkflowDetailTabSummary({ workflowId: row.workflow.id }))}
      >
        {row.workflow.name || 'Untitled Workflow'}
      </Typography>
      <Typography className={classes.rowRoles} component="span" variant="caption">
        Workflow
      </Typography>
    </div>
  );
}

/** A skill inserts as a skill chip, so only one with a published version can be selected. */
function SkillDeliverableRow({
  onToggle,
  row,
  selected,
}: {
  onToggle: () => void;
  row: SkillArtifactRow;
  selected: boolean;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const isPinnable = Boolean(getPinnedSkill(row));

  return (
    <div className={classes.row}>
      <AlineTooltip arrow placement="top" title={isPinnable ? '' : UNPUBLISHED_SKILL_TOOLTIP}>
        <span>
          <Checkbox
            checked={selected}
            className={classes.rowCheckbox}
            disabled={!isPinnable}
            size="small"
            onChange={onToggle}
          />
        </span>
      </AlineTooltip>
      <span className={classes.rowIcon}>
        <Sparkles size={16} />
      </span>
      <Typography
        className={classes.rowTitleLink}
        onClick={() => navigate(link.AdminAgentSkillDetail({ skillId: row.skill.id }))}
      >
        {row.skill.name || 'Untitled skill'}
      </Typography>
      <Typography className={classes.rowRoles} component="span" variant="caption">
        Skill
      </Typography>
    </div>
  );
}

function rowKey(row: DocumentArtifactRow): string {
  return `${row.agentMessageId}:${row.document.id}`;
}

function reportRowKey(row: ReportArtifactRow): string {
  return `${row.agentMessageId}:${row.report.id}`;
}

function taskRowKey(row: TaskArtifactRow): string {
  return `${row.agentMessageId}:${row.task.id}`;
}

function workflowRowKey(row: WorkflowArtifactRow): string {
  return `${row.agentMessageId}:${row.workflow.id}`;
}

function skillRowKey(row: SkillArtifactRow): string {
  return `${row.agentMessageId}:${row.skill.id}`;
}

function selectableRowKey(row: SelectableArtifactRow): string {
  switch (row.__typename) {
    case 'ThreadDocumentArtifact':
      return rowKey(row);
    case 'ThreadReportArtifact':
      return reportRowKey(row);
    case 'ThreadTaskArtifact':
      return taskRowKey(row);
    case 'ThreadWorkflowArtifact':
      return workflowRowKey(row);
    case 'ThreadSkillArtifact':
      return skillRowKey(row);
  }
}

/** The published version a chip pins, the same one the `/` picker offers. */
function getPinnedSkill({ skill }: SkillArtifactRow): PinnedSkill | null {
  if (!skill.currentVersion || !skill.isComplete) return null;
  return {
    description: skill.currentVersion.description,
    name: skill.currentVersion.name,
    skillId: skill.id,
    versionId: skill.currentVersion.id,
  };
}

function toPromptInsertion(rows: SelectableArtifactRow[]): PromptInsertion {
  const insertion: PromptInsertion = { mentions: [], skills: [] };
  for (const row of rows) {
    if (getIsSkillArtifact(row)) {
      const skill = getPinnedSkill(row);
      if (skill) insertion.skills.push({ scope: row.skill.scope, skill });
    } else insertion.mentions.push(toMention(row));
  }
  return insertion;
}

function formatArtifactRoles(row: DocumentArtifactRow): string {
  const roles = row.kindList.length > 0 ? row.kindList : [Apollo.AgentMessageJoinKind.Mentioned];
  return roles.map(kind => ROLE_LABEL_BY_KIND[kind]).join(', ');
}

function toDocumentMention(row: DocumentArtifactRow): DocumentMentionElement {
  return {
    children: [{ text: '' }],
    documentId: row.document.id,
    documentName: row.document.name,
    mentionTypeName: 'document',
    type: mentionType,
    value: row.document.name,
  };
}

function toMention(row: MentionableArtifactRow): TMentionElement {
  switch (row.__typename) {
    case 'ThreadDocumentArtifact':
      return toDocumentMention(row);
    case 'ThreadReportArtifact':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'report',
        reportId: row.report.id,
        reportName: row.report.name || 'Untitled Report',
        type: mentionType,
        value: row.report.name || 'Untitled Report',
      } satisfies ReportMentionElement;
    case 'ThreadTaskArtifact':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'task',
        taskId: row.task.id,
        taskName: row.task.title || 'Untitled Task',
        type: mentionType,
        value: row.task.title || 'Untitled Task',
      } satisfies TaskMentionElement;
    case 'ThreadWorkflowArtifact':
      return {
        children: [{ text: '' }],
        mentionTypeName: 'workflow',
        type: mentionType,
        value: row.workflow.name || 'Untitled Workflow',
        workflowId: row.workflow.id,
        workflowName: row.workflow.name || 'Untitled Workflow',
      } satisfies WorkflowMentionElement;
  }
}

function openArtifactRow(row: DocumentArtifactRow): void {
  openLink(createDocumentRoute(row.document.id));
}

function getIsDocumentArtifact(row: ArtifactRow): row is DocumentArtifactRow {
  return row.__typename === 'ThreadDocumentArtifact';
}

function getIsReportArtifact(row: ArtifactRow): row is ReportArtifactRow {
  return row.__typename === 'ThreadReportArtifact';
}

function getIsTaskArtifact(row: ArtifactRow): row is TaskArtifactRow {
  return row.__typename === 'ThreadTaskArtifact';
}

function getIsWorkflowArtifact(row: ArtifactRow): row is WorkflowArtifactRow {
  return row.__typename === 'ThreadWorkflowArtifact';
}

function getIsSkillArtifact(row: ArtifactRow): row is SkillArtifactRow {
  return row.__typename === 'ThreadSkillArtifact';
}

function BulkActionButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const { classes } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title={label}>
      <IconButton
        aria-label={label}
        className={classes.bulkActionButton}
        size="small"
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </AlineTooltip>
  );
}
